import React, {useEffect} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {
    accessConstants,
    filterConstants,
    authUtils,
    HTMLContent,
    contentUtils,
    LoadChallengeCodeAction,
    LoadQuestionsAction,
    LoadPollsAction,
    LoadedChallengeCodeAction,
    LoadedQuestionsAction,
    LoadedPollsAction,
    LoadQuestionPracticeSkillsAction,
    LoadedPracticeAction,
    LoadPracticeAction
} from "mindsets-js-sdk";

import NoAccess from "../../common/NoAccess";
import EditButton from "./components/EditButton";
import CorrectAnswer from "./components/CorrectAnswer";
import Summary from "./components/Summary";
import SummaryBonus from "./components/SummaryBonus";
import SkillsReference from "./components/SkillsReference";
import {GetQuestionPracticeSkillPDFDataAction} from "../../actions/question_actions";

export default function () {
    const me = useSelector(state => state.me);
    const dispatch = useDispatch();
    const params = useParams();
    const challenge_code = useSelector(state => state.challenge_code);
    const practice = useSelector(state => state.practice);
    const questions = useSelector(state => state.questions);
    const question_practice_skills = useSelector(state => state.question_practice_skills);
    useEffect(() => {
        dispatch(LoadChallengeCodeAction(params.challenge_code, {'selectors[0]': 'latest_challenge_version_name'}));
        dispatch(LoadPracticeAction(params.practice_code, {'selectors[0]': 'practice_skills'})).then(practice => {
            dispatch(LoadQuestionPracticeSkillsAction({
                'filters[challenge_code][0]': filterConstants.EXPRESSION_EQUAL + '=' + params.challenge_code,
                'filters[practice_id][0]': filterConstants.EXPRESSION_EQUAL + '=' + practice.id,
                'selectors[1]': 'questions',
                limit: 0
            }));
        });
        dispatch(LoadQuestionsAction({
            'selectors[0]': 'by_code',
            'filters[challenge_code][0]': filterConstants.EXPRESSION_EQUAL + '=' + params.challenge_code,
            order_by: 'number'
        }));
        dispatch(LoadPollsAction({
            'filters[challenge_code][0]': filterConstants.EXPRESSION_EQUAL + '=' + params.challenge_code,
            'filters[type][0]': filterConstants.EXPRESSION_EQUAL + '=inline',
            'selectors[0]': 'poll_options',
            'limit': 0
        }));

        return () => {
            dispatch(LoadedPracticeAction(null));
            dispatch(LoadedChallengeCodeAction(null));
            dispatch(LoadedQuestionsAction([]));
            dispatch(LoadedPollsAction([]));
        }
    }, []);
    if (!me) {
        return null;
    }
    if (authUtils.iCant(me, [accessConstants.PERMISSION_CAN_SEE_RUBRICS])) {
        return (
            <NoAccess/>
        );
    }
    if (!challenge_code || !practice || !question_practice_skills.length || !questions.length) {
        return null;
    }

    return (
        <div className='container section'>
            <h1>{practice.name} rubric for the Challenge "{challenge_code.latest_challenge_version_name}"</h1>
            <div className='d-flex justify-content-end flex-gap-5'>
                <EditButton/>
                <button
                    className='button'
                    onClick={event => {
                        event.preventDefault();
                        dispatch(GetQuestionPracticeSkillPDFDataAction(params.challenge_code, params.practice_code)).then(buffer => {
                            let a = document.createElement('a');
                            const blob = new Blob([buffer], {'type': 'application/pdf'});
                            a.href = window.URL.createObjectURL(blob);
                            a.download = challenge_code.latest_challenge_version_name + ' rubric.pdf';
                            a.click();
                            window.close();
                        });
                    }}>Download PDF
                </button>
            </div>
            <div>
                {questions.map(question => {
                    const question_practice_skills_for_question = question_practice_skills.filter(qps => qps.question_code === question.code);
                    if (question_practice_skills_for_question.length) {
                        const trs = question_practice_skills_for_question.map(qpsq => {
                            const practice_skill = practice.practice_skills.find(practice_skill => qpsq.practice_skill_id === practice_skill.id);

                            let mode;
                            if (qpsq.mode === 'correct_answer') {
                                mode = 'Grade by Correct Answer';
                            } else if (qpsq.mode === 'ai') {
                                mode = 'Grade with AI';
                            } else if (qpsq.mode === 'attempt') {
                                mode = 'Grade by attempt';
                            }

                            let dependency;
                            if (Array.isArray(qpsq.questions) && qpsq.questions.length) {
                                const questions_dep = questions.filter(q => qpsq.questions.includes(q.code));
                                dependency = questions_dep.map(q => 'Q' + q.number).join(', ');
                            } else {
                                dependency = 'n/a';
                            }

                            let instructions;
                            if (qpsq.mode === 'correct_answer') {
                                instructions = <CorrectAnswer
                                    question={question}
                                    question_practice_skill={qpsq}/>;
                            } else if (qpsq.mode === 'ai') {
                                instructions = qpsq.grading_instructions.map((gi, points) => {
                                    const str = '<strong>' + points + ' point' + (points === 1 ? '' : 's') + '</strong>: ' + gi.replaceAll("\n", "<br/>");
                                    const string = contentUtils.processStringContent(str, {convert_spelling: false});

                                    return (
                                        <HTMLContent key={points} html_str={string}/>);
                                });
                            } else if (qpsq.mode === 'attempt') {
                                instructions = 'n/a';
                            }

                            return (
                                <tr key={qpsq.id}>
                                    <td className='align-middle text-center'>
                                        <div className='no-wrap' title={practice_skill ? practice_skill.name : 'no data'}>{practice_skill ? practice_skill.code : 'failed to load data'}</div>
                                    </td>
                                    <td className='align-middle text-center'>{qpsq.dok}</td>
                                    <td className='align-middle text-center'>{qpsq.domain}</td>
                                    <td className='align-middle text-center'>{mode}</td>
                                    <td className='align-middle text-center'>{dependency}</td>
                                    <td className='align-middle text-center'>{qpsq.max_points}</td>
                                    <td className='align-middle'>{instructions}</td>
                                </tr>
                            );
                        });

                        return (
                            <div className="table-responsive mb-4" key={question.id}>
                                <table className='table table-bordered table-sm'>
                                    <tbody>
                                    <tr>
                                        <td className='align-middle text-center'>
                                            <div style={{fontSize: '22px', fontWeight: 'bold'}}>
                                                {question.number ? 'Q' + question.number : ''}
                                            </div>
                                            <div>{question.is_bonus ? '(BONUS)' : ''}</div>
                                        </td>
                                        <td colSpan={6}>
                                            <strong>
                                                <HTMLContent
                                                    html_str={contentUtils.processStringContent(question.title, {convert_spelling: false})}/>
                                            </strong>
                                            <div>{question.answer_type}</div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{fontWeight: 'bold'}} className='align-middle text-center'>Skill</td>
                                        <td style={{fontWeight: 'bold'}} className='align-middle text-center'>DOK</td>
                                        <td style={{fontWeight: 'bold'}} className='align-middle text-center'>Domain
                                        </td>
                                        <td style={{fontWeight: 'bold'}} className='align-middle text-center'>Grading
                                            mode
                                        </td>
                                        <td style={{fontWeight: 'bold'}} className='align-middle text-center'>Depends
                                            on
                                        </td>
                                        <td style={{fontWeight: 'bold'}} className='align-middle text-center'>Max
                                            points
                                        </td>
                                        <td style={{fontWeight: 'bold'}} className='align-middle text-center'>Grading
                                            instructions OR Fixed correct answer OR
                                            n/a
                                        </td>
                                    </tr>
                                    {trs}
                                    </tbody>
                                </table>
                            </div>
                        );
                    } else {
                        return (
                            <div key={question.id}>Q{question.number} does not have any skills assigned</div>
                        );
                    }
                })}
            </div>
            <Summary/>
            <SummaryBonus/>
            <SkillsReference/>
        </div>
    );
}
